class SheetDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-sheet-dialog]');
    this.handle = this.querySelector('[data-sheet-handle]');
    this.titleElement = this.querySelector('[data-sheet-title]');
    this.defaultTitle = this.titleElement?.textContent;
    this.inert = !this.isOpen;

    this.listeners = new AbortController();
    const { signal } = this.listeners;
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-sheet-close], [data-sheet-overlay]')) this.close();
    }, { signal });
    // Captured on window so this runs before document-level Escape handlers (e.g. the cart
    // drawer's) and can stop them closing whatever sits underneath this panel too.
    window.addEventListener('keydown', (event) => {
      if (!this.isOpen) return;
      const nestedDialog = this.querySelector('dialog[open]');
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (!nestedDialog) this.close();
      }
      if (event.key === 'Tab' && !nestedDialog) this.keepFocusInside(event);
    }, { capture: true, signal });
    this.initSwipeToClose(signal);
  }

  disconnectedCallback() {
    this.listeners?.abort();
    if (this.isOpen) this.close();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  // Same signature as slide-drawer so drawer-show can open either without caring which it has.
  show(_selectedTab, trigger) {
    this.open(trigger);
  }

  open(trigger) {
    this.trigger = trigger;
    if (this.titleElement) this.titleElement.textContent = this.defaultTitle;
    if (!this.isOpen) {
      this.setAttribute('open', '');
      this.inert = false;
      this.syncScrollLock();
      this.dispatchEvent(new CustomEvent('sheet:open'));
    }
    window.waitForPaint().then(() => {
      if (this.isOpen) this.dialog?.querySelector('[data-sheet-close]')?.focus();
    });
  }

  close() {
    if (!this.isOpen) return;
    // Move focus out before going inert — inert drops focus from anything inside, and hiding
    // a focused element (e.g. the overlay that was just tapped) is what Chrome warns about.
    const returnFocus = this.trigger?.querySelector('button') || this.trigger;
    if (returnFocus?.isConnected) returnFocus.focus();
    this.removeAttribute('open');
    this.inert = true;
    this.syncScrollLock();
    this.dispatchEvent(new CustomEvent('sheet:close'));
  }

  syncScrollLock() {
    window.waitForPaint().then(() => {
      const lock = this.isOpen ? window.enableScrollLock : window.disableScrollLock;
      if (typeof lock === 'function') lock(this);
    });
  }

  keepFocusInside(event) {
    const controls = [...this.dialog.querySelectorAll(
      'button:not(:disabled), a[href], input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, iframe, [tabindex]:not([tabindex^="-"])',
    )].filter((control) => !control.closest('[inert], [hidden]') && control.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    const focusIsOutside = !controls.includes(document.activeElement);
    const focusReachedBoundary =
      (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last);
    if (!first || (!focusIsOutside && !focusReachedBoundary)) return;
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }

  isSwipeable() {
    return getComputedStyle(this).getPropertyValue('--sheet-swipeable').trim() !== '0';
  }

  initSwipeToClose(signal) {
    const { dialog: panel, handle } = this;
    if (!panel || !handle) return;
    const threshold = 50;
    let startY = null;
    let lastY = null;
    let movingUp = false;
    const reset = () => {
      startY = null;
      lastY = null;
      movingUp = false;
      panel.style.transition = '';
      panel.style.transform = '';
    };
    panel.addEventListener('touchstart', (event) => {
      if (!this.isSwipeable()) return;
      const touchY = event.touches[0].clientY;
      startY = touchY <= handle.getBoundingClientRect().bottom ? touchY : null;
      lastY = startY;
      movingUp = false;
    }, { passive: true, signal });
    panel.addEventListener('touchmove', (event) => {
      if (startY === null) return;
      // Follow the thumb 1:1 (no transition) while dragging; touchend either completes the
      // close from wherever the finger let go, or clears this so the panel snaps back open.
      const touchY = event.touches[0].clientY;
      movingUp = touchY < lastY;
      lastY = touchY;
      const deltaY = Math.max(0, touchY - startY);
      event.preventDefault();
      panel.style.transition = 'none';
      panel.style.transform = `translateY(${deltaY}px)`;
    }, { passive: false, signal });
    panel.addEventListener('touchend', (event) => {
      if (startY === null) return;
      const deltaY = Math.max(0, event.changedTouches[0].clientY - startY);
      // Releasing while still moving upward always snaps back open, even past the threshold —
      // only a release while moving down (or at rest) past the threshold closes the panel.
      const shouldClose = !movingUp && deltaY > threshold;
      reset();
      if (shouldClose) this.close();
    }, { signal });
    panel.addEventListener('touchcancel', reset, { signal });
  }
}
if (!customElements.get('sheet-dialog')) customElements.define('sheet-dialog', SheetDialog);
